/**
 * Tenant binding: resolve the store's fixture keys (roster emails, catalog labels,
 * patient MRNs and names, shift-center and reason names) to the seeded tenant's server ids,
 * and link staged plan visits to the real visits the seed placed. Everything by
 * natural key, so the same store works against the clone and the real database.
 */
import type { DemoState, PlanVisit, ServerBindings, ServerSnapshot, VisitTypeKind } from "@caredna-marlin/utils/homeHealthDemo/state";
import { toDateKey } from "@caredna-marlin/utils/homeHealthDemo/engine/clock";
import { rosterEntries } from "./roster";

export interface OrgCatalog {
  visitTypes: { id: number; name: string }[];
  shiftCenters: { id: number; name: string }[];
  serviceLines: { id: number; name: string }[];
}

export function resolveBindings(state: DemoState, snapshot: ServerSnapshot, catalog: OrgCatalog): ServerBindings {
  const providerIdsByHandle: Record<string, number> = {};
  for (const entry of rosterEntries(state)) {
    const p = matchProvider(snapshot.providers, entry);
    if (p) providerIdsByHandle[entry.handle] = p.id;
  }
  const visitTypeIdsByKind: Partial<Record<VisitTypeKind, number>> = {};
  for (const vt of Object.values(state.config.visitTypes)) {
    const match = catalog.visitTypes.find((x) => x.name.trim().toLowerCase() === vt.label.toLowerCase());
    if (match) visitTypeIdsByKind[vt.kind] = match.id;
  }
  const patientIdsByRef: Record<string, number> = {};
  const episodeIdsByRef: Record<string, number> = {};
  for (const patient of Object.values(state.patients)) {
    // By MRN when the seed gave one; by name for a patient keyed in during the demo.
    const match = (patient.mrn ? snapshot.patients.find((x) => x.mrn === patient.mrn) : undefined) ?? snapshot.patients.find((x) => x.firstName === patient.firstName && x.lastName === patient.lastName);
    if (!match) continue;
    patientIdsByRef[patient.ref] = match.id;
    const episode = snapshot.episodes.find((e) => e.patientId === match.id && e.active);
    if (episode) episodeIdsByRef[patient.ref] = episode.id;
  }
  const shiftCenterIdsByName = Object.fromEntries(catalog.shiftCenters.map((sc) => [sc.name, sc.id]));
  const cancellationReasonIdsByName = Object.fromEntries(snapshot.cancellationReasons.map((r) => [r.description, r.id]));
  const serviceLineId = catalog.serviceLines.find((sl) => sl.name === "Home Health")?.id ?? catalog.serviceLines[0]?.id;
  return {
    ...state.meta.bindings,
    serviceLineId,
    providerIdsByHandle,
    visitTypeIdsByKind,
    patientIdsByRef,
    episodeIdsByRef,
    shiftCenterIdsByName,
    cancellationReasonIdsByName,
  };
}

/** The tenant counts as seeded when the roster resolved and at least one real visit exists. */
export function isTenantSeeded(bindings: ServerBindings, snapshot: ServerSnapshot): boolean {
  return Object.keys(bindings.providerIdsByHandle).length >= 6 && snapshot.visitResources.length > 0;
}

/**
 * Link staged plan visits (status real/completed) to real visits: same patient,
 * same visit type, same branch-zone day. Completed real visits mark the plan
 * visit completed; a linked visit that later reads canceled becomes missed. The
 * store dates each plan visit where the seed placed it (`store/scheduledDays.ts`),
 * so the same day is the same visit by construction.
 */
export function linkPlanVisits(state: DemoState, snapshot: ServerSnapshot): Record<string, PlanVisit> {
  const tz = state.config.branch.timeZone;
  const { patientIdsByRef, visitTypeIdsByKind } = state.meta.bindings;
  const out: Record<string, PlanVisit> = {};
  let changed = false;
  const byVisit = new Map<number, (typeof snapshot.visitResources)[number]>();
  for (const vr of snapshot.visitResources) if (!byVisit.has(vr.visitId)) byVisit.set(vr.visitId, vr);
  // One real visit delivers one plan visit: those already linked are taken first.
  const claimed = new Set<number>(Object.values(state.planVisits).flatMap((pv) => (pv.realVisitId !== undefined ? [pv.realVisitId] : [])));
  for (const [id, pv] of Object.entries(state.planVisits)) {
    const episode = state.episodes[pv.episodeId];
    const patientId = episode ? patientIdsByRef[episode.patientRef] : undefined;
    const typeId = visitTypeIdsByKind[pv.visitTypeKind];
    if (patientId === undefined || typeId === undefined || pv.status === "placeholder" || pv.status === "canceled") {
      out[id] = pv;
      continue;
    }
    const real = pv.realVisitId !== undefined ? byVisit.get(pv.realVisitId) : [...byVisit.values()].find((vr) => !claimed.has(vr.visitId) && vr.patientId === patientId && vr.visitTypeId === typeId && toDateKey(new Date(vr.startIso), tz) === pv.targetDate);
    if (!real) {
      out[id] = pv;
      continue;
    }
    const status: PlanVisit["status"] = real.eventStatus === "complete" ? "completed" : real.eventStatus === "canceled" ? "missed" : pv.status === "completed" ? "completed" : "real";
    const providerHandle = Object.entries(state.meta.bindings.providerIdsByHandle).find(([, pid]) => pid === real.providerId)?.[0] ?? pv.providerHandle;
    claimed.add(real.visitId);
    const next = { ...pv, realVisitId: real.visitId, status, providerHandle };
    if (next.realVisitId !== pv.realVisitId || next.status !== pv.status || next.providerHandle !== pv.providerHandle) changed = true;
    out[id] = next;
  }
  return changed ? out : state.planVisits;
}

/**
 * A roster entry binds to the tenant's provider by display name first ("Dana Reyes"
 * is unique on this roster), then by email. The name path does not depend on the
 * providers query returning the user relation.
 */
export function matchProvider<P extends { displayName: string; email?: string }>(providers: P[], entry: { displayName: string; email: string }): P | undefined {
  const name = entry.displayName.trim().toLowerCase();
  const email = entry.email.trim().toLowerCase();
  return providers.find((p) => p.displayName.trim().toLowerCase() === name) ?? providers.find((p) => p.email?.trim().toLowerCase() === email);
}
